import { TagInfo } from "@/apis/table/types";
import { SelectedState } from "@/components/mapping/retrieval-results/types";
import { tagKey } from "@/components/mapping/utils";
import DragTagModal from "@/components/modals/drag-tag";
import { useMappingInfos } from "@/components/modals/mapping-info/hooks";
import { useRestoreSelection } from "@/jspreadsheet/jspreadsheet";
import { useMainStore } from "@/stores/main";
import { Fact, useMappingStore } from "@/stores/mapping";
import { useModalStore } from "@/stores/modal";
import useDragTagging, { useShouldDragTag } from "./use-drag-tag";

export const useTagging = ({
  fact,
  toast,
}: {
  fact?: Fact;
  toast?: {
    onSuccess?: () => void;
    onError?: () => void;
  };
}) => {
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const factToTag = fact ?? selectedFact;
  const { tableId, workbookId, handleAddTag, handleRemoveTag } =
    useMappingInfos({
      toToast: false,
    });
  const dimension = useMappingStore((state) => state.dimension.dimension);

  const shouldDragTag = useShouldDragTag();
  const { push, pop } = useModalStore((state) => state.modals);
  const { mutate: dragTag } = useDragTagging({
    onSuccess: () => {
      pop();
      toast?.onSuccess?.();
    },
    onError: () => {
      pop();
      toast?.onError?.();
    },
  });
  const currentSelection = useMainStore((state) => state.selection);
  const { restoreSelection } = useRestoreSelection();

  const handleClick = (tag: TagInfo, isSelected: SelectedState) => async () => {
    const tagInfo: TagInfo = {
      name: tag.name,
      label: tag.label,
      prefix: tag.prefix,
      type: dimension === "LINE_ITEM" ? "LINE_ITEM" : "MEMBER",
      id: tag.id,
      parent: tag.parent,
      roleKey: tag.roleKey ?? "",
    };
    const isSelectable =
      (tag.type === "MEMBER" || tag.type === "LINE_ITEM") && !!tag.roleKey;

    if (factToTag && isSelectable) {
      if (isSelected === "self") {
        await handleRemoveTag(tagInfo, workbookId, tableId, factToTag)();
      } else {
        if (shouldDragTag) {
          if (!dragTag) return;
          push(DragTagModal, {
            cellAddresses: currentSelection.current?.address,
            koLabel: tag.label,
            onConfirm: () => {
              dragTag({ tagKey: tagKey(tag.prefix, tag.name) });
              restoreSelection();
            },
            onCancel: () => {
              pop();
              restoreSelection();
            },
          });
        } else {
          handleAddTag(tagInfo, workbookId, tableId, factToTag)()
            .then(() => {
              toast?.onSuccess?.();
            })
            .catch((error) => {
              toast?.onError?.();
            });
        }
      }
    }
  };

  return { handleClick };
};
